import DOMPurify from 'dompurify';
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useFair } from '../../api/FairProvider';
import { CloseIcon } from '../icons';

const DISMISSED_KEY = 'vv.fairMessageDismissed';

/**
 * The dismissed message itself is stored rather than a flag, so a fair that
 * changes its message shows the new one even to visitors who closed the old.
 */
function readDismissed(): string | null {
  try {
    return window.localStorage.getItem(DISMISSED_KEY);
  } catch {
    // Storage blocked: the message shows again on the next visit.
    return null;
  }
}

function writeDismissed(message: string): void {
  try {
    window.localStorage.setItem(DISMISSED_KEY, message);
  } catch {
    // As above.
  }
}

/**
 * `fair.message` is admin-authored HTML, kept to inline text and links: it runs
 * along a single line, and a block or an image would break the strip. Links
 * open in a new tab, whatever the markup says.
 */
function sanitizeMessage(html: string): string {
  const fragment = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['a', 'b', 'strong', 'i', 'em', 'u', 'span'],
    ALLOWED_ATTR: ['href'],
    RETURN_DOM_FRAGMENT: true,
  });

  for (const link of fragment.querySelectorAll('a')) {
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
  }

  const container = document.createElement('div');
  container.append(fragment);

  return container.innerHTML;
}

/**
 * The markup for the repeats: still clickable, since on a wide screen most of
 * the copies in view are repeats, but out of the tab order so the keyboard
 * meets each link once.
 */
function withoutTabStops(html: string): string {
  const container = document.createElement('div');
  container.innerHTML = html;

  for (const link of container.querySelectorAll('a')) {
    link.setAttribute('tabindex', '-1');
  }

  return container.innerHTML;
}

/** Scrolls at a steady pace however long the message is. */
const PIXELS_PER_SECOND = 50;

/**
 * The fair's announcement as a ticker across the top of every page.
 *
 * The strip is two identical halves, each holding as many copies of the
 * message as it takes to span the bar, and moves by exactly one half per
 * cycle: the second half lands where the first began, so the loop has no seam
 * and the bar is never partly empty, however short the message or wide the
 * screen.
 */
export function FairMessageBar(): ReactElement | null {
  const { t } = useTranslation();
  const { fair } = useFair();
  // Legacy messages pad themselves with runs of &nbsp; to space out the old
  // marquee; the strip spaces its copies itself, so the padding is dropped.
  const message = (fair?.message ?? '').replace(/^(?:\s|&nbsp;)+|(?:\s|&nbsp;)+$/g, '');
  const [dismissed, setDismissed] = useState(readDismissed);

  const html = useMemo(() => (message === '' ? '' : sanitizeMessage(message)), [message]);
  const repeatHtml = useMemo(() => withoutTabStops(html), [html]);
  const visible = html !== '' && dismissed !== message;

  const viewportRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState({ copies: 1, copyWidth: 0 });

  useEffect(() => {
    const viewport = viewportRef.current;

    if (!visible || viewport === null) {
      return;
    }

    // Also fires once on observe, which takes the first measurement.
    const observer = new ResizeObserver(() => {
      const copyWidth = viewport.querySelector<HTMLElement>('.fair-message')?.offsetWidth ?? 0;

      if (copyWidth === 0) {
        return;
      }

      const copies = Math.max(1, Math.ceil(viewport.clientWidth / copyWidth));

      setFit((previous) =>
        previous.copies === copies && previous.copyWidth === copyWidth
          ? previous
          : { copies, copyWidth },
      );
    });

    observer.observe(viewport);

    return (): void => {
      observer.disconnect();
    };
  }, [visible, html]);

  if (!visible) {
    return null;
  }

  const halfWidth = fit.copies * fit.copyWidth;
  const style = {
    '--ticker-duration': `${String(halfWidth === 0 ? 30 : halfWidth / PIXELS_PER_SECOND)}s`,
  } as CSSProperties;

  const copy = (index: number): ReactElement => (
    <span
      key={index}
      // Every copy after the first exists only to fill the loop, so screen
      // readers and the keyboard skip it.
      // Not `inert`: that would make their links unclickable too.
      {...(index > 0 && { 'aria-hidden': true })}
      className="fair-message shrink-0 pr-40 whitespace-nowrap"
      // Sanitised in sanitizeMessage: inline tags and hrefs only.
      dangerouslySetInnerHTML={{ __html: index === 0 ? html : repeatHtml }}
    />
  );

  return (
    <div
      role="region"
      aria-label={t('fairMessage.label')}
      className="relative z-30 bg-prize text-white"
    >
      <div className="flex items-center">
        <div ref={viewportRef} className="min-w-0 flex-1 overflow-hidden py-2 text-sm font-medium">
          <div className="fair-ticker flex w-max" style={style}>
            {Array.from({ length: fit.copies * 2 }, (_, index) => copy(index))}
          </div>
        </div>
        <button
          type="button"
          aria-label={t('common.close')}
          onClick={() => {
            writeDismissed(message);
            setDismissed(message);
          }}
          className="shrink-0 self-stretch bg-prize px-3 text-white transition-opacity hover:opacity-80"
        >
          <CloseIcon className="size-4" />
        </button>
      </div>
    </div>
  );
}
